import { Button, Card, CardContent, Grid, IconButton } from '@material-ui/core'
import { AddCircle, Edit } from '@material-ui/icons'
import { Typography } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { getMachinesByUserId } from '../../../actions/machineActions'
import defaultMachineThumb from '../../../assets/img/default-machine.png'
import {
  PROJECT_CURRENT_STEP,
  PROJECT_EXIT_REQUEST,
  PROJECT_UPDATE_RESET,
} from '../../../constants/projectConstants'
import { useDateFormatter } from '../../../hooks/useDateFormatter'
import { Context } from '../../../providers/ProjectContextProvider'
import FormContainer from '../../Form/FormContainer'
import Loader from '../../Loader/Loader'
import Message from '../../Message/Message'

interface SelectMachineProps {
  setShowAddMachine: (val: boolean) => void
  selectedMachineId: string
  setSelectedMachineId: (machineId: string) => void
  setMachineToEdit: (val: Machine | undefined) => void
  isProgramDetailsModal?: boolean
  setShowProgrammingModal?: (val: boolean) => void
  handleProgramDetailsNextClick?: () => void
}

interface MachineProps {
  machine: Machine | undefined
  selectedMachineId: string
  toggleSelectedMachine: (id: string) => void
}

const SelectMachine = ({
  setShowAddMachine,
  selectedMachineId,
  setSelectedMachineId,
  setMachineToEdit,
  isProgramDetailsModal,
  setShowProgrammingModal,
  handleProgramDetailsNextClick,
}: SelectMachineProps) => {
  const dispatch = useDispatch()

  const { setShowNotifyUser } = useContext(Context)

  const [machinesSet, setMachinesSet] = useState(false)

  const { userInfo } = useSelector(
    (state: { userLogin: UserLogin }) => state.userLogin
  )
  const { loading, machines, error } = useSelector(
    (state: RootStateOrAny) => state.machineList
  )
  const { project } = useSelector(
    (state: RootStateOrAny) => state.projectDetails
  )
  const { success: successUpdate } = useSelector(
    (state: RootStateOrAny) => state.projectUpdate
  )

  useEffect(() => {
    if (successUpdate) {
      dispatch({ type: PROJECT_UPDATE_RESET })
    }

    if (!machinesSet && Object.keys(project).length !== 0) {
      dispatch(getMachinesByUserId(project.user))
      setMachinesSet(true)
    }
  }, [
    dispatch,
    userInfo,
    successUpdate,
    setMachinesSet,
    machinesSet,
    setMachineToEdit,
    project,
  ])

  const EmptyMachineState = () => {
    return (
      <Typography
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        There are currently no machines in your library. Add a new machine to
        proceed.
      </Typography>
    )
  }

  const Machine = ({
    machine,
    toggleSelectedMachine,
    selectedMachineId,
  }: MachineProps) => {
    const handleEditClick = (
      machine: Machine,
      e: React.MouseEvent<HTMLButtonElement>
    ) => {
      e.stopPropagation()
      setMachineToEdit(machine)
      setShowAddMachine(true)
    }

    const date = useDateFormatter(machine ? machine.updatedAt : new Date())

    return machine ? (
      <div
        className={`card-wrapper ${
          machine._id === selectedMachineId ? 'selected' : ''
        }`}
        onClick={() => toggleSelectedMachine(machine._id)}
      >
        <Card className={`machine white-card`}>
          <div
            className={`thumbnail-container ${
              !machine.thumbnail.id && 'no-thumbnail'
            }`}
            style={{
              backgroundImage: machine.thumbnail.id
                ? `url(${process.env.REACT_APP_API}/api/thumbnail/${machine.thumbnail.id})`
                : `url(${defaultMachineThumb})`,
            }}
          ></div>
          <IconButton
            className="hover-edit"
            onClick={(e) => handleEditClick(machine, e)}
          >
            <Edit />
          </IconButton>

          <CardContent>
            <div className="content">
              <h4>{machine.name}</h4>
              <div className="fs10" style={{ minHeight: 15 }}>
                {machine.make}
              </div>
              <div className="fs10" style={{ minHeight: 15 }}>
                {machine.model}
              </div>

              <div className="flex space-between last-updated">
                <p>
                  <strong className="primary">Last updated:</strong>
                </p>
                <p>{date}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    ) : (
      <></>
    )
  }

  const handleSkipStep = () => {
    setSelectedMachineId('')

    if (isProgramDetailsModal) {
      handleProgramDetailsNextClick?.()
    } else {
      dispatch({
        type: PROJECT_CURRENT_STEP,
        payload: 4,
      })
    }
  }

  const toggleSelectedMachine = (id: string) => {
    setSelectedMachineId(id === selectedMachineId ? '' : id)
  }

  const handleSaveAndExit = () => {
    if (userInfo.isAdmin) {
      setShowNotifyUser(true)
    } else {
      dispatch({
        type: PROJECT_EXIT_REQUEST,
      })
    }
  }

  const handleAddNewMachineClick = () => {
    setSelectedMachineId('')
    setShowAddMachine(true)
    setMachineToEdit(undefined)
  }

  return (
    <div className="select-machine">
      <FormContainer>
        <div className="flex space-between align-center m-b-30">
          <h4 className="m-b-0">
            Select machine{' '}
            {userInfo.isAdmin && project.userName && `for ${project.userName}`}
          </h4>
          <div>
            <Button
              className="m-l-16"
              color="primary"
              onClick={handleAddNewMachineClick}
              startIcon={<AddCircle />}
              variant="contained"
            >
              Add new machine
            </Button>
          </div>
        </div>

        <Grid
          container
          spacing={1}
          className={`scroll-container ${machines.length > 6 && 'scroll'}`}
        >
          {loading ? (
            <Loader />
          ) : error ? (
            <Message variant="danger">{error}</Message>
          ) : machines.length > 0 ? (
            machines.map((machine: Machine) => (
              <Grid item md={4} sm={6} key={machine._id}>
                <Machine
                  machine={machine}
                  toggleSelectedMachine={toggleSelectedMachine}
                  selectedMachineId={selectedMachineId}
                />
              </Grid>
            ))
          ) : (
            <EmptyMachineState />
          )}
        </Grid>

        <div className="flex flex-end m-t-40 m-b-30">
          <p>
            Don't have the machine information at hand? &nbsp;You can{' '}
            <span className="link" onClick={handleSkipStep}>
              skip this step for now.
            </span>
          </p>
        </div>

        <div className="actions flex space-between">
          {!isProgramDetailsModal ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveAndExit}
            >
              Save and exit
            </Button>
          ) : (
            <div></div>
          )}

          <div>
            {!isProgramDetailsModal ? (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() =>
                    dispatch({
                      type: PROJECT_CURRENT_STEP,
                      payload: 2,
                    })
                  }
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  style={{ marginLeft: 20 }}
                  onClick={() =>
                    dispatch({
                      type: PROJECT_CURRENT_STEP,
                      payload: 4,
                    })
                  }
                >
                  Next
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setShowProgrammingModal?.(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  style={{ marginLeft: 20 }}
                  onClick={handleProgramDetailsNextClick}
                >
                  Next
                </Button>
              </>
            )}
          </div>
        </div>
      </FormContainer>
    </div>
  )
}

export default SelectMachine
